import { toFixedWithoutRounding } from "@constants/common";
import { TimeframeOption } from "@constants/types";

/**
 * Constants for tooltip messages
 */
export const STRATEGY_REPORTS_TOOLTIP =
  "7d trailing APR | Check 'Strategy Reports' section for details";

const TIMEFRAME_MAP: Record<string, string> = {
  "7D": "7D",
  "1M": "1M",
  "3M": "3M",
  "6M": "6M",
  YTD: "YTD",
  ALL: "ALL",
};

/**
 * Result type for smart APY calculation
 */
export interface SmartApyResult {
  smartAPY: string;
  apyTooltipInfo: string;
}

/**
 * Get tooltip text for timeframe-based APR (chart data)
 * @param activeTimeframe - The active timeframe option
 * @returns Formatted tooltip text
 */
export const getTimeframeTooltipText = (
  activeTimeframe?: TimeframeOption
): string => {
  const timeframeLabel = TIMEFRAME_MAP[activeTimeframe || "3M"] || "3M";
  return `${timeframeLabel} realised APR post fees`;
};

/**
 * Calculate smart APY by comparing chart data (timeframe APR) with strategy reports (avgAPY)
 * Returns the higher value with appropriate tooltip based on the source
 *
 * @param avgApyFromReports - Average APY from strategy reports JSON files
 * @param timeframeApr - APR from chart data (performance data)
 * @param activeTimeframe - Active timeframe option for chart data
 * @returns Smart APY result with formatted value and tooltip info
 *
 * @example
 * // When strategy reports value is higher
 * calculateSmartApy(11.3, 10.5, "3M")
 * // Returns: { smartAPY: "11.30%", apyTooltipInfo: "7d trailing APR | Check 'Strategy Reports' section for details" }
 *
 * @example
 * // When chart data value is higher
 * calculateSmartApy(10.5, 11.3, "3M")
 * // Returns: { smartAPY: "11.30%", apyTooltipInfo: "3M realised APR post fees" }
 */
export const calculateSmartApy = (
  avgApyFromReports: number | null,
  timeframeApr: number | null,
  activeTimeframe?: TimeframeOption
): SmartApyResult => {
  // Normalize inputs to ensure they're valid numbers
  const avgApy =
    typeof avgApyFromReports === "number" && !isNaN(avgApyFromReports)
      ? avgApyFromReports
      : null;
  const timeframe =
    typeof timeframeApr === "number" &&
    !isNaN(timeframeApr) &&
    timeframeApr !== null
      ? timeframeApr
      : null;

  // Both values available - use the higher one
  if (avgApy !== null && timeframe !== null) {
    const higherValue = Math.max(avgApy, timeframe);
    const isTimeframeHigher = timeframe >= avgApy;

    // Determine tooltip based on which source provides the higher value
    const tooltipText = isTimeframeHigher
      ? getTimeframeTooltipText(activeTimeframe)
      : STRATEGY_REPORTS_TOOLTIP;

    return {
      smartAPY: `${higherValue.toFixed(2)}%`,
      apyTooltipInfo: tooltipText,
    };
  }

  // Only timeframe APR available (chart data)
  if (timeframe !== null) {
    return {
      smartAPY: `${toFixedWithoutRounding(timeframe.toString(), 2)}%`,
      apyTooltipInfo: getTimeframeTooltipText(activeTimeframe),
    };
  }

  // Only avgAPY from reports available (strategy reports)
  if (avgApy !== null) {
    return {
      smartAPY: `${toFixedWithoutRounding(avgApy.toString(), 2)}%`,
      apyTooltipInfo: STRATEGY_REPORTS_TOOLTIP,
    };
  }

  // Neither available - return fallback
  return {
    smartAPY: "-",
    apyTooltipInfo: STRATEGY_REPORTS_TOOLTIP,
  };
};
